import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, db, isDuoUid } from './firebase';

export const FOREIGN_KEY = 'duoplate.foreignAccount';
export const JOIN_KEY = 'duoplate.joinCode';
import type { Duo, Member, Task, UserProfile } from './lib/types';

interface AppState {
  ready: boolean;
  user: User | null;
  profile: UserProfile | null;
  profileLoaded: boolean;
  duo: Duo | null;
  members: Member[];
  tasks: Task[];
  me: Member | null;
  partner: Member | null;
}

const Ctx = createContext<AppState | null>(null);

// Right after createDuo/joinDuo the first listener can be refused for a moment while the new
// membership propagates. A refused listener is dead, so re-subscribe a few times with backoff.
function retrying(subscribe: (onError: () => void) => () => void, attempts = 4): () => void {
  let unsub = () => {};
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const start = (left: number, delay: number) => {
    unsub = subscribe(() => {
      if (stopped || left <= 0) return;
      timer = setTimeout(() => start(left - 1, delay * 2), delay);
    });
  };
  start(attempts, 800);
  return () => { stopped = true; if (timer) clearTimeout(timer); unsub(); };
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [duo, setDuo] = useState<Duo | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        // Accounts from other apps in the shared Auth pool are signed straight back out.
        if (u && !isDuoUid(u.uid)) {
          try { sessionStorage.setItem(FOREIGN_KEY, '1'); } catch { /* storage blocked */ }
          void signOut(auth);
          return;
        }
        setUser(u);
        setReady(true);
      }),
    [],
  );

  useEffect(() => {
    setProfile(null);
    setProfileLoaded(false);
    if (!user) return;
    return onSnapshot(
      doc(db, 'users', user.uid),
      (s) => { setProfile(s.exists() ? (s.data() as UserProfile) : null); setProfileLoaded(true); },
      () => setProfileLoaded(true),
    );
  }, [user]);

  const duoId = profile?.duoId;
  useEffect(() => {
    setDuo(null); setMembers([]); setTasks([]);
    if (!duoId) return;
    const subs = [
      retrying((onErr) => onSnapshot(doc(db, 'duos', duoId), (s) => setDuo(s.exists() ? ({ id: s.id, ...s.data() } as Duo) : null), onErr)),
      retrying((onErr) => onSnapshot(collection(db, 'duos', duoId, 'members'), (s) =>
        setMembers(s.docs.map((d) => ({ uid: d.id, ...d.data() }) as Member)), onErr)),
      retrying((onErr) => onSnapshot(collection(db, 'duos', duoId, 'tasks'), (s) =>
        setTasks(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Task).sort((a, b) => a.time.localeCompare(b.time))), onErr)),
    ];
    return () => subs.forEach((u) => u());
  }, [duoId]);

  const me = members.find((m) => m.uid === user?.uid) ?? null;
  const partner = members.find((m) => m.uid !== user?.uid) ?? null;

  return (
    <Ctx.Provider value={{ ready, user, profile, profileLoaded, duo, members, tasks, me, partner }}>
      {children}
    </Ctx.Provider>
  );
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}
